'use client';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useMessageContext } from '@ui/components/organisms/message';
import { ContentType, ContextUserType } from '@ui/constants/context';
import PostItem from './post-item';
import { CommentList, Separator } from '@ui/components';
import { ReplyRefProvider } from '@ui/contexts';
import { PostItemSkeleton } from './post-item-skeleton';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';
import { sendGAEvent } from '@next/third-parties/google';
import { PostItemMainRef } from './post-item-main-ref';
import { useAnalytics } from '@ui/analytics';

interface Props {
  userType: ContextUserType;
  contentType: ContentType;
  main_post_ref: string;
  isDynamicRoute?: boolean;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
}

export const PostView = ({
  userType,
  contentType,
  main_post_ref,
  isDynamicRoute,
  creatorData,
  moderators_ids,
}: Props) => {
  const messageState = useMessageContext(userType);
  const scrollId = useSearchParams().get('scroll');
  const commentRef = useRef<HTMLDivElement>(null);
  const { capture } = useAnalytics();

  useEffect(() => {
    if (userType !== 'creator' && messageState.singleMessage) {
      // Track the post view event for non-creators
      sendGAEvent('event', 'PostViewPage', {
        evet_label: 'Post View',
        postId: main_post_ref,
      });

      // PostHog tracking
      capture('post_viewed', {
        post_id: main_post_ref,
        user_type: userType,
        content_type: contentType,
        is_dynamic_route: isDynamicRoute,
        timestamp: new Date().toISOString(),
      });
    }
  }, [userType, messageState.singleMessage]);

  useEffect(() => {
    if (scrollId && messageState.singleMessage) {
      const timer = setTimeout(() => {
        const commentElement = commentRef.current;
        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [scrollId, messageState.singleMessage]);

  if (messageState.isLoadingSingleMessage) {
    return (
      <div className="mx-auto flex w-full max-w-[876px]">
        <PostItemSkeleton />
      </div>
    );
  }

  if (messageState.isSingleMessageError) {
    return (
      <div className="m-auto max-w-sm">
        <div className="flex flex-col justify-center space-y-4">
          <div className="text-center">
            <p className="font-nunito text-korDarkGrey text-3xl font-extrabold">
              {messageState?.singleMessageError?.status === 403
                ? 'Oops, post is restricted.'
                : messageState?.singleMessageError?.status === 422
                ? 'Content Removed'
                : 'Something went wrong'}
            </p>
            <p className="font-nunito text-korDarkGrey/40">
              {messageState?.singleMessageError?.status === 422
                ? 'This content is no longer available.'
                : messageState?.singleMessageError?.data?.error || 'Error loading post...'}
            </p>
          </div>
          <Separator className="" />
        </div>
      </div>
    );
  }

  if (!messageState.singleMessage) {
    return <div>No post found</div>;
  }

  const parentPost = messageState.singleMessage.parent_post;
  const rootPost = messageState.singleMessage.main_post;

  return (
    <MessageUpdateFormProvider>
      <>
        <MessageUpdateFormPortal userType={userType} main_post_ref={main_post_ref} />

        <div className="mx-auto flex w-full max-w-[876px] flex-col space-y-4">
          {rootPost && (
            <PostItemMainRef
              mainPostRef={rootPost}
              userType={userType}
              userId={messageState.user_id}
              communityPostRoute={messageState.communityPostRoute}
              communityCommentRoute={messageState.communityCommentRoute}
              communityReplyRoute={messageState.communityReplyRoute}
              query={messageState.query}
              contentType={contentType}
              fullDiscussionRoute={parentPost?.slug}
              isDynamicRoute={isDynamicRoute}
              communityDetails={
                messageState.community_details || messageState.singleMessage.community_details
              }
              moderators_ids={moderators_ids}
            />
          )}

          <div ref={commentRef}>
            <PostItem
              key={messageState.singleMessage.id}
              post={messageState.singleMessage}
              userType={userType}
              userId={messageState.user_id}
              creator_id={
                messageState?.singleMessage?.community_details?.creator_id ??
                messageState.singleMessage.creator_id
              }
              communityPostRoute={messageState.communityPostRoute}
              isOwnPage
              query={messageState.query}
              contentType={contentType}
              isDynamicRoute={isDynamicRoute}
              communityDetails={
                messageState.community_details || messageState.singleMessage.community_details
              }
              moderators_ids={moderators_ids}
            />
          </div>

          {contentType !== 'reply' && !messageState.singleMessage.draft && (
            <p className="font-nunito text-sm font-semibold">
              {contentType === 'post' ? 'Comments' : 'Replies'}
            </p>
          )}

          {contentType !== 'reply' && !messageState.singleMessage.draft && (
            <ReplyRefProvider>
              <CommentList
                contentType={contentType}
                userType={userType}
                post={messageState.singleMessage}
                creator_id={
                  messageState?.singleMessage?.community_details?.creator_id ??
                  messageState.singleMessage.creator_id
                }
                user_id={messageState.user_id}
                main_post_ref={main_post_ref}
                enableReply={contentType === 'post'}
                isDynamicRoute={isDynamicRoute}
                creatorData={creatorData}
                moderators_ids={moderators_ids}
              />
            </ReplyRefProvider>
          )}
        </div>
      </>
    </MessageUpdateFormProvider>
  );
};
